import { Link } from 'react-router';

import { useAdmin } from '@/app/admin-session';
import { canAccessAdminPath } from '@/components/admin/nav';
import { DetailSection, Field, FieldGrid, StatTile } from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import { type AdminTalentDetail } from '@/contracts';
import { useLocale, useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import { formatCount, useFormatSar } from '@/lib/format';
import { relativeTime } from '@/lib/format-time';
import { useSubscriptionStatusLabel } from '@/lib/labels';
import { planSubscriptionTone } from '@/lib/status';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';
import type { AdminTalentSubscription } from '@/contracts';

export function PlanSection({ talent }: { talent: AdminTalentDetail }) {
  const t = useTranslations('admin.talentDetail');
  const statusLabel = useSubscriptionStatusLabel();
  const formatSar = useFormatSar();
  const subscriptions = talent.subscriptions ?? [];

  const subscriptionColumns: ReadonlyArray<DataTableColumn<AdminTalentSubscription>> = [
    {
      key: 'plan',
      header: t('subscriptions.plan'),
      cellClassName: 'font-semibold text-navy',
      cell: (subscription) => subscription.planName,
    },
    {
      key: 'period',
      header: t('subscriptions.period'),
      cellClassName: 'num text-conc-600',
      cell: (subscription) =>
        `${isoDate(subscription.startsAt) ?? ''} – ${isoDate(subscription.endsAt) ?? ''}`,
    },
    {
      key: 'amount',
      header: t('subscriptions.amount'),
      cellClassName: 'num text-conc-600',
      cell: (subscription) =>
        subscription.amountSar !== undefined ? formatSar(subscription.amountSar) : '—',
    },
    {
      key: 'status',
      header: t('subscriptions.status'),
      cell: (subscription) => (
        <StatusBadge tone={planSubscriptionTone(subscription.status)}>
          {statusLabel(subscription.status)}
        </StatusBadge>
      ),
    },
  ];

  return (
    <DetailSection title={t('section.plan')}>
      <FieldGrid className="mb-4">
        <Field label={t('field.currentPlan')}>
          {talent.plan ? talent.plan.name : t('freePlan')}
        </Field>
      </FieldGrid>
      {subscriptions.length === 0 ? (
        <p className="text-[12.5px] text-conc-400">{t('subscriptions.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            inline
            caption={t('subscriptions.caption')}
            rows={subscriptions}
            columns={subscriptionColumns}
            rowKey={(subscription) => `${subscription.planId}-${subscription.startsAt}`}
          />
        </div>
      )}
    </DetailSection>
  );
}

export function ActivitySection({ talent }: { talent: AdminTalentDetail }) {
  const t = useTranslations('admin.talentDetail');
  const { locale } = useLocale();
  const admin = useAdmin();
  const canOpenSupport = canAccessAdminPath('/support', admin.permissions);

  return (
    <DetailSection title={t('section.activity')}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <StatTile
          label={t('field.views')}
          value={talent.profileViews !== undefined ? formatCount(talent.profileViews) : '—'}
        />
        <StatTile
          label={t('field.lastViewed')}
          value={
            talent.lastViewedAt ? (
              <time dateTime={talent.lastViewedAt} className="text-[13px]">
                {relativeTime(new Date(talent.lastViewedAt), locale)}
              </time>
            ) : (
              '—'
            )
          }
        />
        <StatTile
          label={t('field.tickets')}
          value={
            talent.supportTicketCount !== undefined ? formatCount(talent.supportTicketCount) : '—'
          }
        />
      </div>
      {/* The inbox has no requester filter yet, so this opens it unfiltered. */}
      {canOpenSupport && (talent.supportTicketCount ?? 0) > 0 && (
        <Link
          to="/support"
          className="mt-3 inline-flex text-[12.5px] font-semibold text-pitch-700 hover:underline"
        >
          {t('openSupport')}
        </Link>
      )}
    </DetailSection>
  );
}
